import { useEffect, useState } from "react";
import {
    fetchAOV,
    fetchCustomerSummary,
    fetchROAS,
    fetchRevenue,
    fetchTopProducts,
    type AOVResult,
    type CustomerSummary,
    type ROASResult,
    type RevenueResult,
    type Settings,
    type TopProductsResult,
} from "./api";
import { AdsView } from "./components/AdsView";
import { DateRangePicker } from "./components/DateRangePicker";
import { MetricCard } from "./components/MetricCard";
import { OrdersView } from "./components/OrdersView";
import { RevenueChart } from "./components/RevenueChart";
import { TopProducts } from "./components/TopProducts";

type View = "analytics" | "orders" | "ads";

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n: number) => new Date(Date.now() - n * 86_400_000).toISOString().slice(0, 10);

const fmt = (n: number, currency: string) =>
    n.toLocaleString(undefined, { style: "currency", currency, maximumFractionDigits: 0 });

export function Dashboard({ settings, onLogout }: { settings: Settings; onLogout: () => void }) {
    const [view, setView] = useState<View>("analytics");
    const [from, setFrom] = useState(daysAgo(30));
    const [to, setTo] = useState(today());

    const [revenue, setRevenue] = useState<RevenueResult | null>(null);
    const [aov, setAov] = useState<AOVResult | null>(null);
    const [top, setTop] = useState<TopProductsResult | null>(null);
    const [customers, setCustomers] = useState<CustomerSummary | null>(null);
    const [roas, setRoas] = useState<ROASResult | null>(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);

    useEffect(() => {
        if (view !== "analytics") return;
        let cancelled = false;
        setLoading(true);
        setErr(null);
        Promise.all([
            fetchRevenue(settings, from, to),
            fetchAOV(settings, from, to),
            fetchTopProducts(settings, from, to, 10),
            fetchCustomerSummary(settings, from, to),
            fetchROAS(settings, from, to),
        ])
            .then(([r, a, t, c, ro]) => {
                if (cancelled) return;
                setRevenue(r);
                setAov(a);
                setTop(t);
                setCustomers(c);
                setRoas(ro);
            })
            .catch((e) => !cancelled && setErr((e as Error).message))
            .finally(() => !cancelled && setLoading(false));
        return () => {
            cancelled = true;
        };
    }, [settings, from, to, view]);

    return (
        <div className="min-h-screen px-6 py-8 max-w-7xl mx-auto">
            <header className="flex items-center justify-between mb-8 flex-wrap gap-4">
                <div className="flex items-center gap-6 flex-wrap">
                    <div>
                        <h1 className="text-2xl font-semibold">Okiru Analytics</h1>
                        <p className="text-sm text-okiru-muted">Coffee, ground in numbers.</p>
                    </div>
                    <nav className="flex items-center gap-1 bg-okiru-panel border border-okiru-muted/15 rounded-lg p-1">
                        <TabButton active={view === "analytics"} onClick={() => setView("analytics")}>
                            Analytics
                        </TabButton>
                        <TabButton active={view === "ads"} onClick={() => setView("ads")}>
                            Ads
                        </TabButton>
                        <TabButton active={view === "orders"} onClick={() => setView("orders")}>
                            Orders
                        </TabButton>
                    </nav>
                </div>
                <div className="flex items-center gap-3">
                    {view !== "orders" && (
                        <DateRangePicker
                            from={from}
                            to={to}
                            onChange={(f, t) => {
                                setFrom(f);
                                setTo(t);
                            }}
                        />
                    )}
                    <button
                        onClick={onLogout}
                        className="text-xs px-3 py-1.5 rounded-md border border-okiru-muted/30 hover:border-red-400 hover:text-red-400 transition"
                    >
                        Logout
                    </button>
                </div>
            </header>

            {(() => {
                if (view === "orders") return <OrdersView settings={settings} />;
                if (view === "ads") return <AdsView settings={settings} from={from} to={to} />;
                return (
                    <>
                        {err && (
                            <div className="bg-red-950 border border-red-700 text-red-200 px-4 py-3 rounded-lg mb-6 text-sm">
                                {err}
                            </div>
                        )}

                        {loading ? (
                            <div className="text-okiru-muted">Loading…</div>
                        ) : (
                            <>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                                    <MetricCard
                                        label="Net revenue"
                                        value={revenue ? fmt(revenue.totals.net_revenue, revenue.currency) : "—"}
                                        sub={revenue ? `${revenue.totals.order_count} orders` : undefined}
                                    />
                                    <MetricCard
                                        label="AOV"
                                        value={aov ? fmt(aov.aov, aov.currency) : "—"}
                                        sub={aov ? `${aov.order_count} orders` : undefined}
                                    />
                                    <MetricCard
                                        label="Repeat rate"
                                        value={customers ? `${(customers.repeat_rate * 100).toFixed(1)}%` : "—"}
                                        sub={customers ? `${customers.returning_customers} returning` : undefined}
                                    />
                                    <MetricCard
                                        label="Ad spend"
                                        value={roas ? fmt(roas.blended.spend, roas.currency) : "—"}
                                        sub={roas ? `${roas.blended.attributed_orders} attributed orders` : undefined}
                                    />
                                    <MetricCard
                                        label="Blended ROAS"
                                        value={roas && roas.blended.spend > 0 ? `${roas.blended.roas.toFixed(2)}×` : "—"}
                                        sub={roas ? fmt(roas.blended.revenue, roas.currency) + " revenue" : undefined}
                                    />
                                    <MetricCard
                                        label="New customers"
                                        value={customers ? customers.new_customers.toString() : "—"}
                                        sub={customers ? `${customers.total_customers} total` : undefined}
                                    />
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                    <div className="lg:col-span-2">{revenue && <RevenueChart data={revenue} />}</div>
                                    <div>{top && revenue && <TopProducts data={top} currency={revenue.currency} />}</div>
                                </div>
                            </>
                        )}
                    </>
                );
            })()}
        </div>
    );
}

function TabButton({
    active,
    onClick,
    children,
}: {
    active: boolean;
    onClick: () => void;
    children: React.ReactNode;
}) {
    return (
        <button
            onClick={onClick}
            className={`text-xs px-3 py-1.5 rounded-md transition ${
                active ? "bg-okiru-bg text-okiru-text" : "text-okiru-muted hover:text-okiru-text"
            }`}
        >
            {children}
        </button>
    );
}
